import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";

// Components
import Sidebar from "../Sidebar";
import Header from "../Header";
import RealTimeRequest from "./RealTimeRequest"; // Container

// Context & Services
import { RealTimeProvider } from "./reducer/RealTimeContext";
import { SubscriptionProvider } from "../../context/SubscriptionContext";
import { DataManagerProvider } from "../../context/DataManagerContext";
import { useUserDispatch, useUserState } from "../../context";
import authService from "../../services/authService";
import { mainClient, getWebSocketReady } from "../../apollo";

// Logic imports
import {
  GET_All_USER_DATA,
  GET_RECOMMENDER_INFO,
  GET_SERVER_LIST,
  SUBSCRIBE_CURRENTSTATE,
  SUBSCRIBE_USER_SETTING,
} from "../graphql/manageUser";
import { handleUserCurrentState, writeUserData } from "../cache/userData";
import {
  GET_ALL_ITEMINFO,
  GET_OPTION_INFO,
  SUBSCRIBE_REAL_HOGA,
  SUBSCRIBE_REAL_SIGN,
} from "../graphql/itemInfo";
import {
  saveForeignItem,
  saveHogaData,
  saveItemData,
  saveSignData,
} from "../cache/itemData";
import { INQUIRY_DEPOSIT_ACCOUNT } from "../graphql/depositAccount";

// Main content area - memoized to prevent re-renders from sibling state changes
const MainContent = React.memo(({ loading }: { loading: boolean }) => {
  return (
    <div className="flex-1 flex flex-col min-w-0 min-h-0 border-l border-solid border-gray-300">
      <Header />
      <main className="flex-1 min-h-0 overflow-x-hidden overflow-y-auto px-2 py-1 relative">
        {loading ? (
          <div className="flex h-full items-center justify-center">
            Loading...
          </div>
        ) : (
          <Outlet />
        )}
      </main>
    </div>
  );
});

MainContent.displayName = "MainContent";

// Right sidebar - memoized to prevent re-renders from sibling state changes
const RightSidebar = React.memo(() => {
  return (
    <aside className="w-72 h-full shrink-0 border-l border-solid border-gray-300 flex flex-col overflow-hidden">
      <RealTimeRequest />
    </aside>
  );
});

RightSidebar.displayName = "RightSidebar";

const DashBoard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUserState();
  const dispatch = useUserDispatch();
  const isSmallScreen = useMediaQuery({ maxWidth: 1980 });

  // UI State - loading only (collapsed/logout moved to Sidebar)
  const [loading, setLoading] = useState(true);

  // --- Data Fetching & Subscriptions ---

  // User Data
  const { data: userData } = useQuery(GET_All_USER_DATA, {
    fetchPolicy: "no-cache",
    variables: { isAll: true, withTester: true },
  });

  // Item Data - errorPolicy: 'all'로 부분 데이터도 받을 수 있도록 설정
  const { data: allItemInfoData, error: itemInfoError } = useQuery(GET_ALL_ITEMINFO, {
    errorPolicy: 'all',
  });
  const { data: optionData, error: optionError } = useQuery(GET_OPTION_INFO, {
    errorPolicy: 'all',
  });

  // Auth Check
  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate("/", { replace: true });
      return;
    }
    if (user.userId === null) {
      const userId = localStorage.getItem("userId");
      if (userId === null) {
        navigate("/", { replace: true });
      } else {
        const currentUser = authService.getCurrentUser();
        dispatch({
          type: "LOGIN",
          data: {
            userId: currentUser?.id || userId,
            userName: currentUser?.userName,
            authority: currentUser?.authority
          }
        });
      }
    }
  }, [location]);

  // Data Initialization
  useEffect(() => {
    const itemInfoReady = allItemInfoData || itemInfoError;
    const optionDataReady = optionData || optionError;

    if (userData && itemInfoReady && optionDataReady && loading) {
      const fetchDataFinish = writeUserData(userData.getAllUserData);

      const getAllItemInfo = allItemInfoData?.getAllItemInfo || [];
      if (itemInfoError && !allItemInfoData?.getAllItemInfo) {
        console.warn('아이템 데이터 로드 실패:', itemInfoError.message);
      }

      let callOption: any[] = [];
      let putOption: any[] = [];
      if (optionData?.getOptionInfo) {
        callOption = optionData.getOptionInfo.callOption || [];
        putOption = optionData.getOptionInfo.putOption || [];
      } else if (optionError) {
        console.warn('옵션 데이터 로드 실패:', optionError.message);
      }

      [...getAllItemInfo, ...callOption, ...putOption].map((item) => {
        saveItemData(item);
      });
      saveForeignItem(getAllItemInfo.filter((item: any) => item.type !== 0));
      if (fetchDataFinish) setLoading(false);
    }
  }, [userData, loading, allItemInfoData, itemInfoError, optionData, optionError]);

  // Initial Queries
  useEffect(() => {
    const queries = [GET_RECOMMENDER_INFO, INQUIRY_DEPOSIT_ACCOUNT, GET_SERVER_LIST];
    queries.forEach(query => mainClient.query({ query, fetchPolicy: "cache-first" }));
  }, []);

  // Subscriptions - WebSocket 준비 후 구독 시작
  useEffect(() => {
    let sub: any = null;
    let cancelled = false;

    getWebSocketReady().then(() => {
      if (cancelled) return;
      sub = mainClient.subscribe({ query: SUBSCRIBE_CURRENTSTATE, fetchPolicy: "no-cache" })
        .subscribe((result) => {
          if (result?.data?.subscribeCurrentState) handleUserCurrentState(result.data.subscribeCurrentState);
        });
    });

    return () => { cancelled = true; sub?.unsubscribe(); };
  }, []);

  useEffect(() => {
    let subSign: any = null;
    let subHoga: any = null;
    let cancelled = false;

    getWebSocketReady().then(() => {
      if (cancelled) return;
      subSign = mainClient.subscribe({ query: SUBSCRIBE_REAL_SIGN, fetchPolicy: "no-cache" })
        .subscribe((result) => {
          const signs = result?.data?.subscribeRealSign;
          if (!signs) return;
          if (Array.isArray(signs)) {
            const latest = new Map();
            signs.forEach(s => s && s.code && latest.set(s.code, s));
            latest.forEach(s => saveSignData(s));
          } else if (signs.code) saveSignData(signs);
        });

      subHoga = mainClient.subscribe({ query: SUBSCRIBE_REAL_HOGA, fetchPolicy: "no-cache" })
        .subscribe((result) => {
          if (result?.data?.subscribeReal) saveHogaData(result.data.subscribeReal);
        });
    });

    return () => { cancelled = true; subSign?.unsubscribe(); subHoga?.unsubscribe(); };
  }, []);

  useEffect(() => {
    let sub: any = null;
    let cancelled = false;

    getWebSocketReady().then(() => {
      if (cancelled) return;
      sub = mainClient.subscribe({ query: SUBSCRIBE_USER_SETTING })
        .subscribe((result) => {
          if (result?.data?.subscribeUserSetting) handleUserCurrentState(result.data.subscribeUserSetting);
        });
    });

    return () => { cancelled = true; sub?.unsubscribe(); };
  }, []);

  // --- Render ---
  return (
    <SubscriptionProvider>
      <DataManagerProvider>
      <RealTimeProvider>
        <div className={`flex h-screen bg-gradient-to-br from-slate-50 to-blue-50 overflow-hidden${isSmallScreen ? ' text-xs' : ''}`}>
          <Sidebar />
          <MainContent loading={loading} />
          <RightSidebar />
        </div>
      </RealTimeProvider>
      </DataManagerProvider>
    </SubscriptionProvider>
  );
};

export default DashBoard;
